import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, BedDouble } from "lucide-react";

export type RoomRate = {
  id?: string;
  room_type: string;
  description: string;
  max_occupancy: number;
  number_of_rooms?: number | null;
  single_rate: number | null;
  double_rate: number | null;
  triple_rate: number | null;
  extra_person_rate: number | null;
  currency: string;
  rate_basis: "per_room" | "per_person";
  notes: string;
  is_active: boolean;
};

interface SupplierRoomRatesSectionProps {
  roomRates: RoomRate[];
  onChange: (roomRates: RoomRate[]) => void;
  defaultCurrency?: string;
  isVilla?: boolean;
}

const CURRENCIES = ["USD", "ZAR", "EUR", "GBP", "KES", "TZS", "UGX", "RWF", "BWP", "NAD"];

const emptyRoomRate: RoomRate = {
  room_type: "",
  description: "",
  max_occupancy: 2,
  number_of_rooms: null,
  single_rate: null,
  double_rate: null,
  triple_rate: null,
  extra_person_rate: null,
  currency: "USD",
  rate_basis: "per_room",
  notes: "",
  is_active: true,
};

export function SupplierRoomRatesSection({ 
  roomRates, 
  onChange, 
  defaultCurrency = "USD",
  isVilla = false,
}: SupplierRoomRatesSectionProps) {
  const addRoomRate = () => {
    onChange([
      ...roomRates,
      {
        ...emptyRoomRate,
        currency: defaultCurrency,
        ...(isVilla
          ? {
              room_type: "PRIVATE HIRE",
              rate_basis: "per_room" as const,
              max_occupancy: 0,
              number_of_rooms: null,
            }
          : {}),
      },
    ]);
  };

  const updateRoomRate = (index: number, updates: Partial<RoomRate>) => {
    const updated = roomRates.map((rate, i) => 
      i === index ? { ...rate, ...updates } : rate
    );
    onChange(updated);
  };

  const removeRoomRate = (index: number) => {
    onChange(roomRates.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BedDouble className="h-5 w-5 text-muted-foreground" />
          <Label className="text-base font-semibold">{isVilla ? "Villa Hire" : "Room Rates"}</Label>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addRoomRate}>
          <Plus className="h-4 w-4 mr-1" />
          {isVilla ? "Add Villa" : "Add Room Type"}
        </Button>
      </div>

      {roomRates.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-lg">
          {isVilla
            ? 'No villas added yet. Click "Add Villa" to add pricing.'
            : 'No room rates added yet. Click "Add Room Type" to add pricing.'}
        </p>
      )}

      {roomRates.map((rate, index) => (
        <Card key={index} className="relative">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => removeRoomRate(index)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">{isVilla ? `Villa ${index + 1}` : `Room Type ${index + 1}`}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isVilla ? "Villa Name *" : "Room Type Name *"}</Label>
                <Input
                  value={rate.room_type}
                  onChange={(e) => updateRoomRate(index, { room_type: e.target.value })}
                  placeholder={isVilla ? "e.g., PRIVATE HIRE" : "e.g., Classic Room, Family Suite"}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>{isVilla ? "Maximum Guests *" : "Max Occupancy"}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={isVilla ? 50 : 10}
                    value={rate.max_occupancy}
                    onChange={(e) => updateRoomRate(index, { max_occupancy: parseInt(e.target.value) || 2 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={rate.currency}
                    onValueChange={(value) => updateRoomRate(index, { currency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {isVilla && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Number of Rooms *</Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={rate.number_of_rooms ?? ""}
                    onChange={(e) => updateRoomRate(index, {
                      number_of_rooms: e.target.value ? parseInt(e.target.value) : null,
                    })}
                    placeholder="e.g., 5"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Flat Private Rate (per night) *</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={rate.single_rate ?? ""}
                    onChange={(e) => updateRoomRate(index, {
                      single_rate: e.target.value ? parseFloat(e.target.value) : null,
                    })}
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={rate.description}
                onChange={(e) => updateRoomRate(index, { description: e.target.value })}
                placeholder="e.g., King bed or twin beds, en-suite bathroom"
              />
            </div>

            {!isVilla && (
            <div className="space-y-2">
              <Label>Rate Basis</Label>
              <Select
                value={rate.rate_basis}
                onValueChange={(value: "per_room" | "per_person") => updateRoomRate(index, { rate_basis: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_room">Per Room (per night)</SelectItem>
                  <SelectItem value="per_person">Per Person (per night)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {!isVilla && (
            <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Single Rate</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={rate.single_rate ?? ""}
                  onChange={(e) => updateRoomRate(index, { 
                    single_rate: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Double Rate</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={rate.double_rate ?? ""}
                  onChange={(e) => updateRoomRate(index, { 
                    double_rate: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Triple Rate</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={rate.triple_rate ?? ""}
                  onChange={(e) => updateRoomRate(index, { 
                    triple_rate: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Extra Person Rate</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={rate.extra_person_rate ?? ""}
                  onChange={(e) => updateRoomRate(index, { 
                    extra_person_rate: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder="0.00"
                />
              </div>
            </div>
            </>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={rate.notes}
                onChange={(e) => updateRoomRate(index, { notes: e.target.value })}
                placeholder="e.g., Rates include breakfast, children under 6 stay free"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
